<template>
  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="layout-header">
      <div class="header-left">
        <div class="product-info">
          <IconCircle :size="32" bgColor="var(--icon-product-bg)" color="var(--icon-product-color)" :icon="getProductIcon()" />
          <h2 class="product-name">{{ productDisplayName }}</h2>
        </div>

        <TabBar :tabs="tabs" :active-tab="activeTab" @update:active-tab="handleTabChange" />
      </div>

      <div class="header-right">
        <template v-if="mode === 'view' && activeTab === 'details' && currentUser && currentUser.isAdmin">
          <button class="button danger-outline" @click="openModal('delete-product')">Delete</button>
          <button class="button primary" @click="enterEditMode">Edit Product</button>
        </template>

        <template v-else-if="mode === 'edit' || mode === 'create'">
          <button class="button" @click="cancelEdit">Cancel</button>
          <button class="button primary" @click="saveChanges">
            {{ mode === 'create' ? 'Create Product' : 'Save Changes' }}
          </button>
        </template>
      </div>
    </div>

    <div class="layout-body">
      <div v-if="activeTab === 'details'" class="card-container">
        <CardInputForm title="Basic Info" v-model="draft" :fields="basicInfoFields">
          <template #field-name>
            <TextInput v-model="draft.name" placeholder="Enter product name" />
          </template>
          <template #display-name="{ value }">
            <span :class="{ 'no-data': !value }">{{ value || 'No data' }}</span>
          </template>

          <template #field-productType>
            <SelectInput v-model="draft.productType" :options="productTypeOptions" placeholder="Select product type" />
          </template>
          <template #display-productType="{ value }">
            <span :class="{ 'no-data': !value }">{{ formatProductType(value) || 'No data' }}</span>
          </template>
        </CardInputForm>

        <CardInputForm title="Default Values" v-model="draft" :fields="defaultFields">
          <template #field-defaultDurationMonths>
            <SelectInput v-model="draft.defaultDurationMonths" :options="durationOptions" placeholder="Select duration..." />
          </template>
          <template #display-defaultDurationMonths="{ value }">
            <span :class="{ 'no-data': value === null }">{{ value !== null ? formatDuration(value) : 'No default' }}</span>
          </template>

          <template #field-defaultQuantity>
            <NumericalInput v-model="draft.defaultQuantity" :optional="true" placeholder="Leave empty for no default" />
          </template>
          <template #display-defaultQuantity="{ value }">
            <span :class="{ 'no-data': value === null }">{{ value !== null ? value : 'No default' }}</span>
          </template>

          <template #field-defaultUnitPriceCents>
            <MonetaryInput v-model="draft.defaultUnitPriceCents" :optional="true" placeholder="Leave empty for no default" />
          </template>
          <template #display-defaultUnitPriceCents="{ value }">
            <span :class="{ 'no-data': value === null }">{{ formatUtils.formatCurrency(value) || 'No default' }}</span>
          </template>
        </CardInputForm>

        <CardInputForm title="Invoice Information" v-model="draft" :fields="invoiceFields">
          <template #field-description>
            <TextareaInput v-model="draft.description" placeholder="e.g. A mix of 3 separate drugs." />
          </template>
          <template #display-description="{ value }">
            <div :class="{ 'no-data': !value }" style="white-space: pre-wrap;">{{ value || 'No data' }}</div>
          </template>

          <template #field-insuranceCodes>
            <TextareaInput v-model="draft.insuranceCodes" placeholder="e.g. NDC: ( Tadalafil 00002-4464-30 (75mg), Vardenafil 00085-1934-01 (75mg) )" />
          </template>
          <template #display-insuranceCodes="{ value }">
            <div :class="{ 'no-data': !value }" style="white-space: pre-wrap;">{{ value || 'No data' }}</div>
          </template>

          <template #field-diagnosisCodes>
            <TextareaInput v-model="draft.diagnosisCodes" placeholder="e.g. N50.1 - Vascular Disorders of the Penis&#10;N52.9 - Impotence of Organic Organ&#10;E29.1 - Testicular Hypo Function" />
          </template>
          <template #display-diagnosisCodes="{ value }">
            <div :class="{ 'no-data': !value }" style="white-space: pre-wrap;">{{ value || 'No data' }}</div>
          </template>
        </CardInputForm>
      </div>
      <div v-if="activeTab === 'tickets'">
        <p>Tickets</p>
      </div>
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <DeleteProductModal v-if="modal.id === 'delete-product'" :productId="productId" @close="closeModal" @deleted="handleProductDeleted" />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/app/AppLayout.vue';
import TabBar from '@/components/common/TabBar.vue';
import CardInputForm from '@/components/common/CardInputForm.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import TextareaInput from '@/components/inputs/TextareaInput.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import MonetaryInput from '@/components/inputs/MonetaryInput.vue';
import NumericalInput from '@/components/inputs/NumericalInput.vue';
import PillIcon from 'vue-material-design-icons/Pill.vue';
import CurrencyUsdIcon from 'vue-material-design-icons/CurrencyUsd.vue';
import PackageIcon from 'vue-material-design-icons/Package.vue';
import DeleteProductModal from '@/modals/DeleteProductModal.vue';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';

export default {
  name: 'ProductRecordView',
  computed: {
    productId() {
      return this.$route.params.id;
    },
    productLink() {
      return '/products/' + this.productId;
    },
    breadcrumbs() {
      return [
        { label: 'Products', link: '/products' },
        { label: this.productDisplayName || 'Product', link: this.productLink }
      ];
    },
    productDisplayName() {
      if (this.mode === 'create') return 'New Product';
      if (!this.product) return 'Loading...';
      return this.product.name || 'Unknown Product';
    },
    isReadonly() {
      return this.mode === 'view';
    },
    durationOptions() {
      return [
        { value: 0, label: 'None' },
        { value: 1, label: '1 mo' },
        { value: 2, label: '2 mo' },
        { value: 3, label: '3 mo' },
        { value: 4, label: '4 mo' },
        { value: 5, label: '5 mo' },
        { value: 6, label: '6 mo' },
        { value: 7, label: '7 mo' },
        { value: 8, label: '8 mo' },
        { value: 9, label: '9 mo' },
        { value: 10, label: '10 mo' },
        { value: 11, label: '11 mo' },
        { value: 12, label: '12 mo' }
      ];
    },
    productTypeOptions() {
      return [
        { value: 'medication', label: 'Medication' },
        { value: 'fee', label: 'Fee' },
        { value: 'other', label: 'Other' }
      ];
    },
    basicInfoFields() {
      return [
        {
          name: 'name',
          label: 'Product Name',
          readonly: this.isReadonly || this.mode === 'edit'
        },
        {
          name: 'productType',
          label: 'Product Type',
          readonly: this.isReadonly
        }
      ];
    },
    defaultFields() {
      return [
        {
          name: 'defaultDurationMonths',
          label: 'Default Duration (Months)',
          readonly: this.isReadonly
        },
        {
          name: 'defaultQuantity',
          label: 'Default Quantity',
          readonly: this.isReadonly
        },
        {
          name: 'defaultUnitPriceCents',
          label: 'Default Unit Price',
          readonly: this.isReadonly
        },
      ];
    },
    invoiceFields() {
      return [
        {
          name: 'description',
          label: 'Description',
          readonly: this.isReadonly
        },
        {
          name: 'insuranceCodes',
          label: 'Insurance Codes',
          readonly: this.isReadonly
        },
        {
          name: 'diagnosisCodes',
          label: 'Diagnosis Codes',
          readonly: this.isReadonly
        }
      ];
    },
    tabs() {
      return [
        { key: 'details', label: 'Details' }
      ];
    },
    activeTab() {
      const tab = this.$route.params.tab;
      const validTabs = this.tabs.map(t => t.key);
      if (tab && validTabs.includes(tab)) {
        return tab;
      }
      return 'details';
    }
  },
  data() {
    return {
      product: null,
      isLoading: false,
      mode: 'view',
      draft: {},
      currentUser: null,
      formatUtils,
      modal: {
        open: false,
        id: ''
      }
    };
  },
  created() {
    this.currentUser = queryClient.getQueryData(['user']) || null;
    if (this.productId === 'new') {
      this.enterCreateMode();
    } else {
      this.load();
    }
  },
  methods: {
    async load() {
      try {
        this.isLoading = true;

        const product = await queryClient.fetchQuery({
          queryKey: ['product', this.productId],
          queryFn: () => api.fetchProductById(this.productId)
        });

        this.product = product;
        this.draft = structuredClone(this.product);

      } catch (err) {
        console.error('Failed to fetch product:', err);
        alertService.error('Failed to load product: ' + err.message);
        this.$router.push('/products');
      } finally {
        this.isLoading = false;
      }
    },
    handleTabChange(tabKey) {
      const basePath = `/products/${this.productId}`;
      if (tabKey === 'details') {
        this.$router.push(basePath);
      } else {
        this.$router.push(`${basePath}/${tabKey}`);
      }
    },
    enterEditMode() {
      if (this.activeTab !== 'details') {
        this.$router.push(`/products/${this.productId}`);
      }
      this.draft = structuredClone(this.product);
      this.mode = 'edit';
    },
    enterCreateMode() {
      this.draft = this.getEmptyDraft();
      this.mode = 'create';
    },
    cancelEdit() {
      if (this.mode === 'create') {
        this.$router.push('/products');
      } else {
        this.draft = structuredClone(this.product);
        this.mode = 'view';
      }
    },
    getEmptyDraft() {
      return {
        name: '',
        productType: 'other',
        defaultQuantity: null,
        defaultDurationMonths: null,
        defaultUnitPriceCents: null,
        diagnosisCodes: null,
        description: null,
        insuranceCodes: null
      };
    },
    getProductIcon() {
      const productType = this.draft?.productType || this.product?.productType || 'other';
      switch (productType) {
        case 'medication':
          return PillIcon;
        case 'fee':
          return CurrencyUsdIcon;
        case 'other':
        default:
          return PackageIcon;
      }
    },
    formatDuration(months) {
      if (!months || months === 0) return 'None';
      return `${months} mo`;
    },
    formatProductType(value) {
      const option = this.productTypeOptions.find(opt => opt.value === value);
      return option ? option.label : value;
    },
    transformDraftToPayload(draft) {
      return {
        name: draft.name,
        productType: draft.productType,
        defaultQuantity: draft.defaultQuantity,
        defaultDurationMonths: draft.defaultDurationMonths,
        defaultUnitPriceCents: draft.defaultUnitPriceCents,
        diagnosisCodes: draft.diagnosisCodes,
        description: draft.description,
        insuranceCodes: draft.insuranceCodes
      };
    },
    openModal(id) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      if (event.target.classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    handleProductDeleted() {
      this.closeModal();
      queryClient.invalidateQueries({ queryKey: ['products'] });
      alertService.success('Product deleted successfully');
      this.$router.push('/products');
    },
    async saveChanges() {
      try {
        const payload = this.transformDraftToPayload(this.draft);
        let updatedProduct;

        if (this.mode === 'edit') {
          updatedProduct = await api.updateProduct(this.productId, payload);
          alertService.success('Product updated successfully');
        }

        if (this.mode === 'create') {
          updatedProduct = await api.createProduct(payload);
          alertService.success('Product created successfully');
          this.$router.replace(`/products/${updatedProduct.productId}`);
        }

        // Invalidate caches
        queryClient.invalidateQueries({ queryKey: ['products'] });
        queryClient.invalidateQueries({ queryKey: ['product', this.productId] });

        // Update product
        this.product = updatedProduct;
        this.draft = structuredClone(this.product);
        this.mode = 'view';

      } catch (err) {
        console.error('Failed to save product:', err);
        alertService.error('Failed to save product: ' + err.message);
      }
    }
  },
  components: { AppLayout, TabBar, CardInputForm, IconCircle, TextInput, TextareaInput, SelectInput, MonetaryInput, NumericalInput, DeleteProductModal }
}
</script>

<style scoped>
.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
  flex: 1;
  min-width: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.product-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  line-height: 1.2;
}

.card-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0;
}

.layout-header .button {
  white-space: nowrap;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.button.danger-outline {
  color: #dc2626;
  border-color: #dc2626;
  background: white;
}

.button.danger-outline:hover {
  background-color: #fef2f2;
}
</style>
